import draftToHtml from 'draftjs-to-html';
import { useCallback } from 'react';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import StimulusUsageMessage from '../shared/stimulus-message';
import {
  GUIDED_RESEARCH_TITLE,
  SettingsProps,
} from './guided-research.constants';
import {
  SettingsTitleView,
  SettingsRichtextView,
  SettingsThumbnailView,
} from '../shared/settings-view';
import * as Styled from 'approot/shared/stimulus/form-fields/stimulus-form-guided-research.style';
import {
  COMPONENT_LAYOUT_GRID,
  COMPONENT_LAYOUT_LIST,
  InquisitiveComponentLayout,
} from 'global/constants';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import { spacing24 } from 'approot/shared/styles/spacing.style';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';

const GuidedResearchEditPreviewDrawer = ({
  id,
  sectionId,
  properties,
  stimulusResourceProperties,
  lessonId,
}: SettingsProps) => {
  const { closeDrawer } = useActivityBuilderContext();
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const handleUpdateLayout = useCallback(
    (display: InquisitiveComponentLayout) => {
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              ...properties,
              layout: {
                display,
              },
            },
          },
        },
        properties: {
          ...properties,
          layout: {
            __typename: 'ComponentLayout',
            display,
          },
        },
        typename: 'ActivitiesGuidedResearchComponent',
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  if (!stimulusResourceProperties) return null;

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader
        title={GUIDED_RESEARCH_TITLE}
        onClose={closeDrawer}
      />
      <ActivityBuilderDrawerContent>
        <SettingsGroup>
          <SettingsLabel>Layout</SettingsLabel>
          <CheckableSingle
            variant="pill"
            name="guided-research-display"
            selected={properties.layout?.display || COMPONENT_LAYOUT_GRID}
            options={[
              {
                label: 'Row',
                value: COMPONENT_LAYOUT_GRID,
                'aria-label': 'Set layout to row',
                onClick: () => handleUpdateLayout(COMPONENT_LAYOUT_GRID),
              },
              {
                label: 'Card',
                value: COMPONENT_LAYOUT_LIST,
                'aria-label': 'Set layout to Card',
                onClick: () => handleUpdateLayout(COMPONENT_LAYOUT_LIST),
              },
            ]}
          />
        </SettingsGroup>
        <HrStyled $marginBottom={spacing24} />
        <StimulusUsageMessage
          componentId={id}
          stimulusId={properties.stimulusId || 0}
          contentId={lessonId}
          sectionId={sectionId}
        />
        {stimulusResourceProperties.links.map((link, index) => (
          <Styled.GuidedResearchLink key={index}>
            <Styled.GuidedResearchDisplayWrapper>
              <Styled.GuidedResearchLinkHeader>
                <div>
                  <Styled.GuidedResearchHeading>
                    {index + 1}. {link.title}
                  </Styled.GuidedResearchHeading>
                  {link.description && (
                    <Styled.GuidedResearchRichtext
                      as="div"
                      dangerouslySetInnerHTML={{
                        __html: draftToHtml(link.description),
                      }}
                    />
                  )}
                  <Styled.GuidedResearchText>
                    {link.link}
                  </Styled.GuidedResearchText>
                </div>
              </Styled.GuidedResearchLinkHeader>
            </Styled.GuidedResearchDisplayWrapper>
          </Styled.GuidedResearchLink>
        ))}
        <SettingsTitleView
          title="Stimulus Name"
          value={stimulusResourceProperties.resourceName}
        />
        <SettingsRichtextView
          title="Stimulus Description"
          value={stimulusResourceProperties.description}
        />
        <SettingsThumbnailView
          title="Stimulus Thumbnail"
          imageUrl={
            stimulusResourceProperties.coverImage?.responsive?.[0].urls.at(-1)
              ?.url
          }
        />
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default GuidedResearchEditPreviewDrawer;
